import dayjs, { type Dayjs } from 'dayjs';

export interface AnalyticsIncident {
    startedAt: string;
    resolvedAt?: string;
    severity?: number;
    missingQualityCheck: string;
    primaryImpactedServiceName: string;
}

export interface SeverityLevel {
    id: number;
    name: string;
}

export type TrendRange = 'weekly' | 'monthly';
export type FocusField = 'missingQualityCheck' | 'primaryImpactedServiceName';

/** Window choices for the focus chart. `weekly` is the last 90 days; `monthly` is 12 months. */
export const focusWindows: { value: TrendRange; label: string }[] = [
    { value: 'weekly', label: '90d' },
    { value: 'monthly', label: '12mo' },
];

export const UNKNOWN_SEVERITY = 'Unknown';
export const UNSPECIFIED = 'Unspecified';
const WINDOW_DAYS = 90;
const MONTHLY_MONTHS = 12;

const trendWindowStart = (range: TrendRange, now: Dayjs): Dayjs =>
    range === 'weekly' ? now.subtract(WINDOW_DAYS, 'day') : now.subtract(MONTHLY_MONTHS, 'month');

export interface SeverityBucket {
    id: number | null; // null = no severity recorded (e.g. still-open incident)
    name: string;
}

/** Stack/legend order: defined severities by id ascending (most severe first), then Unknown. */
export const severityBuckets = (levels: SeverityLevel[]): SeverityBucket[] => [
    ...levels
        .slice()
        .sort((a, b) => a.id - b.id)
        .map((level) => ({ id: level.id, name: level.name })),
    { id: null, name: UNKNOWN_SEVERITY },
];

/** The incident's severity id when it matches a known level, else null (Unknown). */
const severityIdOf = (severity: number | undefined, levels: SeverityLevel[]): number | null =>
    levels.some((level) => level.id === severity) ? severity! : null;

const inRange = (iso: string | undefined, start: Dayjs, end: Dayjs): boolean => {
    if (!iso) return false;
    const date = dayjs(iso);
    return date.isValid() && date.isAfter(start) && !date.isAfter(end);
};

/** "4h 12m", "45m", or "—" when there is nothing to average. */
export const formatDuration = (ms: number | null): string => {
    if (ms === null || !Number.isFinite(ms)) return '—';
    const totalMinutes = Math.round(ms / 60_000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

const average = (values: number[]): number | null =>
    values.length === 0 ? null : values.reduce((sum, value) => sum + value, 0) / values.length;

/** Percent change vs the prior window; null when the prior window is empty. */
const deltaPct = (current: number, prior: number): number | null =>
    prior === 0 ? null : Math.round(((current - prior) / prior) * 100);

export interface Kpis {
    mttrMs: number | null;
    mttrDeltaPct: number | null;
    /** The two most-severe levels with their current-window counts, e.g. Critical / Major. */
    topSeverities: { name: string; current: number }[];
    /** Count delta of the single most-severe level vs the prior window. */
    topSeverityDelta: number;
    total: number;
    totalDeltaPct: number | null;
}

export const computeKpis = (
    incidents: AnalyticsIncident[],
    levels: SeverityLevel[],
    now: Dayjs,
): Kpis => {
    const curStart = now.subtract(WINDOW_DAYS, 'day');
    const prevStart = now.subtract(WINDOW_DAYS * 2, 'day');

    const startedIn = (start: Dayjs, end: Dayjs) =>
        incidents.filter((inc) => inRange(inc.startedAt, start, end));
    const current = startedIn(curStart, now);
    const prior = startedIn(prevStart, curStart);

    const top = levels
        .slice()
        .sort((a, b) => a.id - b.id)
        .slice(0, 2);
    const countSev = (list: AnalyticsIncident[], id: number) =>
        list.filter((inc) => inc.severity === id).length;

    const mttrFor = (list: AnalyticsIncident[]) =>
        average(
            list
                .filter((inc) => inc.resolvedAt)
                .map((inc) => dayjs(inc.resolvedAt).diff(dayjs(inc.startedAt)))
                .filter((diff) => diff >= 0),
        );

    const mttrMs = mttrFor(current);
    const mttrPrev = mttrFor(prior);

    return {
        mttrMs,
        mttrDeltaPct: mttrMs !== null && mttrPrev !== null ? deltaPct(mttrMs, mttrPrev) : null,
        topSeverities: top.map((level) => ({
            name: level.name,
            current: countSev(current, level.id),
        })),
        topSeverityDelta: top.length
            ? countSev(current, top[0].id) - countSev(prior, top[0].id)
            : 0,
        total: current.length,
        totalDeltaPct: deltaPct(current.length, prior.length),
    };
};

export interface GroupedSeries {
    labels: string[];
    series: { id: number | null; name: string; data: (number | null)[] }[];
}

interface Bucket {
    start: Dayjs;
    end: Dayjs;
    label: string;
}

/** Calendar buckets covering the trend window; the first bucket is a partial period. */
const buildBuckets = (range: TrendRange, now: Dayjs): Bucket[] => {
    const unit = range === 'weekly' ? 'week' : 'month';
    const labelFormat = range === 'weekly' ? 'M/D' : 'MMM';
    const first = trendWindowStart(range, now).startOf(unit);
    const last = now.startOf(unit);
    const buckets: Bucket[] = [];
    for (let start = first; !start.isAfter(last); start = start.add(1, unit)) {
        buckets.push({ start, end: start.add(1, unit), label: start.format(labelFormat) });
    }
    return buckets;
};

const inTrendWindow = (iso: string | undefined, range: TrendRange, now: Dayjs): boolean =>
    inRange(iso, trendWindowStart(range, now), now);

/** Incident counts per time bucket, split into one series per severity. */
export const bucketVolume = (
    incidents: AnalyticsIncident[],
    levels: SeverityLevel[],
    range: TrendRange,
    now: Dayjs,
): GroupedSeries => {
    const buckets = buildBuckets(range, now);
    const sevs = severityBuckets(levels);
    const counts = new Map<number | null, number[]>(sevs.map((s) => [s.id, buckets.map(() => 0)]));

    incidents.forEach((inc) => {
        if (!inTrendWindow(inc.startedAt, range, now)) return;
        const date = dayjs(inc.startedAt);
        const index = buckets.findIndex((b) => !date.isBefore(b.start) && date.isBefore(b.end));
        if (index >= 0) counts.get(severityIdOf(inc.severity, levels))![index] += 1;
    });

    return {
        labels: buckets.map((b) => b.label),
        series: sevs.map((s) => ({ id: s.id, name: s.name, data: counts.get(s.id)! })),
    };
};

/** Average recovery time (hours) per bucket, by start date of resolved incidents. */
export const bucketMttr = (
    incidents: AnalyticsIncident[],
    range: TrendRange,
    now: Dayjs,
): { labels: string[]; hours: (number | null)[] } => {
    const buckets = buildBuckets(range, now);
    const perBucket: number[][] = buckets.map(() => []);

    incidents.forEach((inc) => {
        if (!inc.resolvedAt || !inTrendWindow(inc.startedAt, range, now)) return;
        const started = dayjs(inc.startedAt);
        const resolved = dayjs(inc.resolvedAt);
        if (!resolved.isValid()) return;
        const index = buckets.findIndex(
            (b) => !started.isBefore(b.start) && started.isBefore(b.end),
        );
        const diff = resolved.diff(started);
        if (index >= 0 && diff >= 0) perBucket[index].push(diff);
    });

    return {
        labels: buckets.map((b) => b.label),
        hours: perBucket.map((diffs) => {
            const avg = average(diffs);
            return avg === null ? null : Math.round((avg / 3_600_000) * 10) / 10;
        }),
    };
};

/** Top drivers within the trend window (90 days or 12 months), ranked by count, split by severity. */
export const paretoBy = (
    incidents: AnalyticsIncident[],
    levels: SeverityLevel[],
    field: FocusField,
    now: Dayjs,
    range: TrendRange = 'weekly',
    limit = 8,
): GroupedSeries => {
    const start = trendWindowStart(range, now);
    const sevs = severityBuckets(levels);
    const groups = new Map<string, Map<number | null, number>>();

    incidents
        .filter((inc) => inRange(inc.startedAt, start, now))
        .forEach((inc) => {
            const key = inc[field]?.trim() || UNSPECIFIED;
            if (!groups.has(key)) groups.set(key, new Map(sevs.map((s) => [s.id, 0])));
            const id = severityIdOf(inc.severity, levels);
            const counts = groups.get(key)!;
            counts.set(id, (counts.get(id) ?? 0) + 1);
        });

    const total = (counts: Map<number | null, number>) =>
        [...counts.values()].reduce((sum, value) => sum + value, 0);

    const ranked = [...groups.entries()]
        .sort(([, a], [, b]) => total(b) - total(a))
        .slice(0, limit)
        .reverse(); // highest at top once Chart.js inverts the y-axis

    return {
        labels: ranked.map(([key]) => key),
        series: sevs.map((s) => ({
            id: s.id,
            name: s.name,
            data: ranked.map(([, counts]) => counts.get(s.id) ?? 0),
        })),
    };
};
